import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Brain,
  Search,
  RefreshCw,
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  X,
  AlertTriangle,
  Eye,
} from 'lucide-react';
import { behavioralPromptsApi, eventsApi, adminApi } from '../../services/api';
import { BehavioralPrompt, IEvent, AppUser } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

// ── Constants ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

const STATUS_COLORS: Record<string, string> = {
  true: 'bg-green-50 text-green-700 border border-green-200',
  false: 'bg-gray-100 text-gray-500 border border-gray-200',
};

function priorityBadgeClass(p: number): string {
  if (p === 1) return 'bg-red-50 text-red-700 border border-red-200';
  if (p === 2) return 'bg-orange-50 text-orange-700 border border-orange-200';
  return 'bg-gray-100 text-gray-600 border border-gray-200';
}

interface FormState {
  eventId: string;
  name: string;
  prompt: string;
  context: string;
  priority: number;
  isActive: boolean;
}

const emptyForm = (eventId = ''): FormState => ({
  eventId,
  name: '',
  prompt: '',
  context: '',
  priority: 1,
  isActive: true,
});

// ── Component ──────────────────────────────────────────────────────────────────

const BehavioralPrompts: React.FC = () => {
  const { isAdmin } = useAuth();

  const [prompts, setPrompts] = useState<BehavioralPrompt[]>([]);
  const [clients, setClients] = useState<AppUser[]>([]);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<BehavioralPrompt | null>(null);

  // View details
  const [viewTarget, setViewTarget] = useState<BehavioralPrompt | null>(null);

  // ── Data fetching ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isAdmin) {
      fetchClients();
    } else {
      fetchEvents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && selectedClientId) {
      fetchEvents(selectedClientId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, selectedClientId]);

  const fetchClients = async () => {
    try {
      const res = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      if (res.data.success && res.data.data) {
        setClients(res.data.data.users || []);
      }
    } catch {
      toast.error('Failed to fetch clients');
    }
  };

  const fetchEvents = async (clientId?: string) => {
    try {
      const params: Record<string, any> = { limit: 100 };
      if (clientId) params.clientId = clientId;
      const res = await eventsApi.getAll(params);
      const evts: IEvent[] = res.data.data || [];
      setEvents(evts);
      if (evts.length > 0) setSelectedEventId(evts[0].eventId);
    } catch {
      toast.error('Failed to fetch events');
    }
  };

  const fetchPrompts = useCallback(async () => {
    if (!selectedEventId) return;
    setLoading(true);
    try {
      const res = await behavioralPromptsApi.getByEvent(selectedEventId);
      setPrompts(res.data.data || []);
    } catch {
      toast.error('Failed to fetch behavioral prompts');
    } finally {
      setLoading(false);
    }
  }, [selectedEventId]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // ── Derived ──────────────────────────────────────────────────────────────────

  const filtered = prompts.filter((p) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return p.name.toLowerCase().includes(q) || p.prompt.toLowerCase().includes(q);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const openAdd = () => {
    if (!selectedEventId) {
      toast.warning('Please select a Knowledge Assistant first');
      return;
    }
    setIsEditing(false);
    setEditingId(null);
    setForm(emptyForm(selectedEventId));
    setModalOpen(true);
  };

  const openEdit = (p: BehavioralPrompt) => {
    setIsEditing(true);
    setEditingId(p._id);
    setForm({
      eventId: p.eventId,
      name: p.name,
      prompt: p.prompt,
      context: p.context || '',
      priority: p.priority,
      isActive: p.isActive,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (form.prompt.trim().length < 20) {
      toast.error('Prompt must be at least 20 characters');
      return;
    }
    try {
      if (isEditing && editingId) {
        await behavioralPromptsApi.update(editingId, form);
        toast.success('Behavioral prompt updated');
      } else {
        await behavioralPromptsApi.create(form);
        toast.success('Behavioral prompt created');
      }
      closeModal();
      fetchPrompts();
    } catch {
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} behavioral prompt`);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await behavioralPromptsApi.delete(deleteTarget._id);
      toast.success('Behavioral prompt deleted');
      setDeleteTarget(null);
      fetchPrompts();
    } catch {
      toast.error('Failed to delete behavioral prompt');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="p-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900">Behavioral Prompts</h1>
        <p className="text-sm text-gray-500 mt-1">
          Configure system prompts that shape how your Knowledge Assistants behave and respond
        </p>
      </div>

      {/* Table Card */}
      <div className="bg-white border border-gray-200 rounded-lg">
        {/* Toolbar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-lg font-semibold text-gray-900">Behavioral Prompts Management</h2>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search prompts"
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-52"
                />
              </div>

              {/* Client dropdown (admin only) */}
              {isAdmin && (
                <select
                  value={selectedClientId}
                  onChange={(e) => {
                    setSelectedClientId(e.target.value);
                    setSelectedEventId('');
                    setPrompts([]);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white min-w-[160px]"
                >
                  <option value="">Select Client</option>
                  {clients.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}

              {/* Event / KA dropdown */}
              <select
                value={selectedEventId}
                onChange={(e) => {
                  setSelectedEventId(e.target.value);
                  setCurrentPage(1);
                }}
                disabled={isAdmin && !selectedClientId}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white min-w-[180px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select KA</option>
                {events.map((e) => (
                  <option key={e.eventId} value={e.eventId}>
                    {e.name}
                  </option>
                ))}
              </select>

              {/* Refresh */}
              <button
                onClick={fetchPrompts}
                disabled={!selectedEventId}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </button>

              {/* Add */}
              <button
                onClick={openAdd}
                disabled={!selectedEventId}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Add Prompt
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Name', 'Prompt', 'Context', 'Priority', 'Status', 'Created', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-sm text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : paginated.length > 0 ? (
                paginated.map((p) => (
                  <tr key={p._id} className="hover:bg-gray-50 transition-colors">
                    {/* Name */}
                    <td className="px-6 py-4 max-w-[160px]">
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900 leading-snug">
                          {p.name}
                        </span>
                      </div>
                    </td>

                    {/* Prompt */}
                    <td className="px-6 py-4 max-w-[260px]">
                      <p className="text-sm text-gray-500 truncate" title={p.prompt}>
                        {p.prompt}
                      </p>
                    </td>

                    {/* Context */}
                    <td className="px-6 py-4 max-w-[220px]">
                      {p.context ? (
                        <p className="text-sm text-gray-500 truncate" title={p.context}>
                          {p.context}
                        </p>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>

                    {/* Priority */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${priorityBadgeClass(p.priority)}`}
                      >
                        {p.priority}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[String(p.isActive)]}`}
                      >
                        {p.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    {/* Created */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500">
                        {new Date(p.createdAt).toLocaleDateString()}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setViewTarget(p)}
                          className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEdit(p)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(p)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center">
                      <Brain className="w-12 h-12 text-gray-300 mb-3" />
                      <p className="text-gray-500 text-sm">
                        {selectedEventId
                          ? 'No behavioral prompts found'
                          : 'Select a Knowledge Assistant to view prompts'}
                      </p>
                      {selectedEventId && (
                        <p className="text-gray-400 text-xs mt-1">
                          Add a new prompt or adjust your search
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {filtered.length === 0
                ? '0 prompts'
                : `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(
                    currentPage * PAGE_SIZE,
                    filtered.length
                  )} of ${filtered.length} prompt${filtered.length !== 1 ? 's' : ''}`}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <span className="text-sm text-gray-600">
                {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add / Edit Modal ─────────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[92vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">
                {isEditing ? 'Edit Behavioral Prompt' : 'Add Behavioral Prompt'}
              </h2>
              <button
                onClick={closeModal}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-6 py-6 space-y-6 flex-1">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <span className="text-red-500 mr-0.5">*</span>Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. General Behaviour of the AI agent"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <span className="text-red-500 mr-0.5">*</span>Prompt
                </label>
                <textarea
                  value={form.prompt}
                  onChange={(e) => setForm((f) => ({ ...f, prompt: e.target.value }))}
                  placeholder="You are a helpful assistant..."
                  rows={5}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-y"
                />
                <p className="mt-1.5 text-xs text-gray-400">
                  Write the system-level instruction that shapes the assistant's behaviour.
                  (Min 20 characters)
                </p>
              </div>

              {/* Context */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Context</label>
                <textarea
                  value={form.context}
                  onChange={(e) => setForm((f) => ({ ...f, context: e.target.value }))}
                  placeholder="Describe when and where this prompt should be applied..."
                  rows={3}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                />
                <p className="mt-1.5 text-xs text-gray-400">
                  Specify the scenario or conditions under which this prompt is activated.
                </p>
              </div>

              {/* Priority + Status */}
              <div className="flex items-start gap-8">
                {/* Priority */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <span className="text-red-500 mr-0.5">*</span>Priority
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={form.priority}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        priority: Math.min(10, Math.max(1, Number(e.target.value))),
                      }))
                    }
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <p className="mt-1.5 text-xs text-gray-400">
                    1 = Highest priority, 10 = Lowest priority
                  </p>
                </div>

                {/* Status toggle */}
                <div className="pt-0.5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
                    className={`relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors text-sm font-medium select-none ${
                      form.isActive ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    <span>{form.isActive ? 'Active' : 'Inactive'}</span>
                    <span
                      className={`relative inline-block w-9 h-5 rounded-full transition-colors ${
                        form.isActive ? 'bg-blue-300' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          form.isActive ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {isEditing ? 'Save Changes' : 'Add Prompt'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Details Modal ───────────────────────────────────────────────── */}
      {viewTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setViewTarget(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[92vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-blue-500" />
                <h2 className="text-base font-semibold text-gray-900">{viewTarget.name}</h2>
              </div>
              <button
                onClick={() => setViewTarget(null)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-6 py-6 space-y-5 flex-1">
              {/* Prompt */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Prompt</p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{viewTarget.prompt}</p>
                </div>
              </div>

              {/* Context */}
              {viewTarget.context && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Context</p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{viewTarget.context}</p>
                  </div>
                </div>
              )}

              {/* Meta row */}
              <div className="flex items-center gap-6 pt-1">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Priority</p>
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${priorityBadgeClass(viewTarget.priority)}`}>
                    {viewTarget.priority}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[String(viewTarget.isActive)]}`}>
                    {viewTarget.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Created</p>
                  <span className="text-sm text-gray-700">{new Date(viewTarget.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => { setViewTarget(null); openEdit(viewTarget); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Edit
              </button>
              <button
                onClick={() => setViewTarget(null)}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation ──────────────────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Delete Behavioral Prompt
                </h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete{' '}
                  <span className="font-medium text-gray-900">"{deleteTarget.name}"</span>?
                </p>
                <p className="text-sm text-gray-500 mt-1">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Prompt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BehavioralPrompts;
